// src/pages/VehicleV2/BillGenerationEntry.jsx
// Design aligned with ProductionBOMListing
// Business logic unchanged: hire bill entry, child-screen cart, submit writes DB

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaUndo,
  FaExternalLinkAlt,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/billGeneration`;
const DEFAULT_ID = 0;
const HIRE_TYPE = "H";

const toNum = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const toInt = (v, def = DEFAULT_ID) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();
const safeJson = (raw, def = null) => {
  try {
    return raw ? JSON.parse(raw) : def;
  } catch {
    return def;
  }
};
const normalizeIds = (arr) =>
  Array.isArray(arr) ? arr.map((x) => toInt(x, 0)).filter((x) => x > 0) : [];

const defaultVehicle = {
  vehicleName: "",
  vehicleType: "",
  vehicleBasis: "",
  regNo: "",
  equipmentNo: "",
  usageCost: "0",
  fuelRate: "0",
  tdsPercent: "0",
};
const defaultParty = { name: "", pan: "", address: "" };
const defaultProgress = {
  totalHrs: "0",
  totalKm: "0",
  breakingHrs: "0",
  hrRate: "0",
  kmRate: "0",
  hrAmount: "0",
  kmAmount: "0",
  breakingAmount: "0",
  driverAdvance: "0",
  bata: "0",
  progressTotal: "0",
  fuelIssued: "0",
  totalDieselCost: "0",
};
const defaultEditable = {
  particulars: "",
  advanceAmt: "0",
  shiftingAmt: "0",
  tdsPercent: "0",
  preparedBy: "",
  sparesDeduction: "0",
  materialDeduction: "0",
  maintenanceReason: "",
  maintenanceCharges: "0",
  otherCharges: "0",
  reasonForDeduction: "",
  includeDieselCost: false,
};

const BillGenerationEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const q = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const vehicleId = toStr(q.get("VehID") || q.get("id") || "");
  const vehicleOwnerType = toStr(q.get("brtype") || HIRE_TYPE).toUpperCase();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const STORAGE_KEY = useMemo(() => `billGen_selection_${vehicleId}`, [vehicleId]);

  const [pageLoading, setPageLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showVehicle, setShowVehicle] = useState(true);
  const [showProgress, setShowProgress] = useState(true);
  const [showSpares, setShowSpares] = useState(true);

  const [billNo, setBillNo] = useState("");
  const [billDate, setBillDate] = useState("");
  const [party, setParty] = useState(defaultParty);
  const [veh, setVeh] = useState(defaultVehicle);
  const [vehMeta, setVehMeta] = useState({ type: HIRE_TYPE, basis: "", subType: "" });
  const [progress, setProgress] = useState(defaultProgress);

  const [particulars, setParticulars] = useState("");
  const [advanceAmt, setAdvanceAmt] = useState("0");
  const [shiftingAmt, setShiftingAmt] = useState("0");
  const [tdsPercent, setTdsPercent] = useState("0");
  const [preparedBy, setPreparedBy] = useState("");
  const [sparesDeduction, setSparesDeduction] = useState("0");
  const [materialDeduction, setMaterialDeduction] = useState("0");
  const [maintenanceReason, setMaintenanceReason] = useState("");
  const [maintenanceCharges, setMaintenanceCharges] = useState("0");
  const [otherCharges, setOtherCharges] = useState("0");
  const [reasonForDeduction, setReasonForDeduction] = useState("");
  const [includeDieselCost, setIncludeDieselCost] = useState(false);

  const [progressIds, setProgressIds] = useState([]);
  const [progressDetIds, setProgressDetIds] = useState([]);
  const [sparesIds, setSparesIds] = useState([]);
  const [sparesDetIds, setSparesDetIds] = useState([]);
  const [advanceIds, setAdvanceIds] = useState([]);
  const [shiftingIds, setShiftingIds] = useState([]);

  const isBusy = pageLoading || submitLoading || refreshing;

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const readClass = `w-full px-4 py-2 border rounded-lg ${
    isDark
      ? "bg-gray-700/40 border-gray-600 text-gray-100"
      : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;
  const panelClass = `p-6 rounded-lg mb-6 ${
    isDark ? "bg-gray-800/50" : "bg-blue-50"
  }`;
  const panelTitleClass = `text-lg font-semibold ${
    isDark ? "text-white" : "text-blue-900"
  }`;
  const ghostBtn = `flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
    isDark
      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
  }`;
  const primaryBtn = `flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`;

  const liveProgressTotal = useMemo(() => {
    return (
      toNum(progress.hrAmount) +
      toNum(progress.kmAmount) +
      toNum(progress.bata) -
      toNum(progress.driverAdvance)
    );
  }, [progress.hrAmount, progress.kmAmount, progress.bata, progress.driverAdvance]);

  const sparesTotalAmount = useMemo(
    () => toNum(sparesDeduction) + toNum(materialDeduction),
    [sparesDeduction, materialDeduction]
  );

  const baseBalanceBeforeTds = useMemo(() => {
    return (
      toNum(liveProgressTotal) +
      toNum(otherCharges) -
      (toNum(advanceAmt) + toNum(shiftingAmt) + toNum(maintenanceCharges) + toNum(sparesTotalAmount))
    );
  }, [liveProgressTotal, otherCharges, advanceAmt, shiftingAmt, maintenanceCharges, sparesTotalAmount]);

  const tdsAmount = useMemo(() => {
    const per = toNum(tdsPercent);
    if (per <= 0) return 0;
    return (per * toNum(baseBalanceBeforeTds)) / 100;
  }, [tdsPercent, baseBalanceBeforeTds]);

  const balancePayable = useMemo(() => {
    let bal = toNum(baseBalanceBeforeTds) - toNum(tdsAmount);
    if (includeDieselCost) bal += toNum(progress.totalDieselCost);
    return bal;
  }, [baseBalanceBeforeTds, tdsAmount, includeDieselCost, progress.totalDieselCost]);

  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const applyVehDetailsToProgress = useCallback((vehDetails) => {
    if (!vehDetails) return;
    const hrAmount = toStr(vehDetails.totalPrgAmount ?? vehDetails.hrAmount ?? "0");
    const kmAmount = toStr(vehDetails.totalamountkm ?? vehDetails.kmAmount ?? "0");
    const bata = toStr(vehDetails.bata ?? "0");
    const driverAdvance = toStr(vehDetails.advance ?? vehDetails.driverAdvance ?? "0");
    setProgress((prev) => ({
      ...prev,
      totalHrs: toStr(vehDetails.totalhour ?? vehDetails.totalHrs ?? prev.totalHrs),
      totalKm: toStr(vehDetails.totalKMHour ?? vehDetails.totalKm ?? prev.totalKm),
      breakingHrs: toStr(vehDetails.breakingHrs ?? prev.breakingHrs),
      hrRate: toStr(vehDetails.hrrateamnt ?? vehDetails.hrRate ?? prev.hrRate),
      kmRate: toStr(vehDetails.rateperkmH ?? vehDetails.kmRate ?? prev.kmRate),
      hrAmount,
      kmAmount,
      breakingAmount: toStr(vehDetails.breakingAmount ?? prev.breakingAmount),
      driverAdvance,
      bata,
      progressTotal: String(toNum(hrAmount) + toNum(kmAmount) + toNum(bata) - toNum(driverAdvance)),
      fuelIssued: toStr(vehDetails.dieselQTY ?? vehDetails.fuelIssued ?? prev.fuelIssued),
      totalDieselCost: toStr(vehDetails.totalDieselAmount ?? vehDetails.totalDieselCost ?? prev.totalDieselCost),
    }));
    sessionStorage.setItem("billGeneration_selectedVehDetails", JSON.stringify(vehDetails));
  }, []);

  const applyIdsFromSelectionObject = useCallback((sel) => {
    if (!sel) return;
    const pIds = normalizeIds(sel.progressIds);
    const pDet = Array.isArray(sel.progressDetIds) ? sel.progressDetIds.map((x) => toInt(x, 0)) : [];
    if (pIds.length) {
      setProgressIds(pIds);
      setProgressDetIds(pDet.length === pIds.length ? pDet : pIds.map(() => DEFAULT_ID));
    }
    const sIds = normalizeIds(sel.sparesIds);
    const sDet = Array.isArray(sel.sparesDetIds) ? sel.sparesDetIds.map((x) => toInt(x, 0)) : [];
    if (sIds.length) {
      setSparesIds(sIds);
      setSparesDetIds(sDet.length === sIds.length ? sDet : sIds.map(() => DEFAULT_ID));
    }
    const aIds = normalizeIds(sel.advanceIds);
    const shIds = normalizeIds(sel.shiftingIds);
    if (aIds.length) setAdvanceIds(aIds);
    if (shIds.length) setShiftingIds(shIds);
    if (sel.advanceAmt != null) setAdvanceAmt(toStr(sel.advanceAmt));
    if (sel.shiftingAmt != null) setShiftingAmt(toStr(sel.shiftingAmt));
    if (sel.sparesDeduction != null) setSparesDeduction(toStr(sel.sparesDeduction));
  }, []);

  const syncFromLocalStorage = useCallback(() => {
    if (!vehicleId) return;
    applyIdsFromSelectionObject(safeJson(localStorage.getItem(STORAGE_KEY), null));
  }, [vehicleId, STORAGE_KEY, applyIdsFromSelectionObject]);

  const loadInit = useCallback(async () => {
    if (!canUse(vehicleId, "Missing vehicleId in URL")) return;
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          vehicleId,
          branchId,
          loginUserId,
          brtype: vehicleOwnerType || HIRE_TYPE,
        },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");

      setBillNo(toStr(data.billNo));
      setBillDate(toStr(data.billDate));
      setParty({
        name: toStr(data?.party?.name),
        pan: toStr(data?.party?.pan),
        address: toStr(data?.party?.address),
      });
      const v = data?.vehicle || {};
      setVeh({
        vehicleName: toStr(v.vehicleName),
        vehicleType: toStr(v.vehicleType),
        vehicleBasis: toStr(v.vehicleBasis),
        regNo: toStr(v.regNo),
        equipmentNo: toStr(v.equipmentNo),
        usageCost: toStr(v.usageCost ?? "0"),
        fuelRate: toStr(v.fuelRate ?? "0"),
        tdsPercent: toStr(v.tdsPercent ?? "0"),
      });
      setVehMeta({
        type: toStr(data?.meta?.type || HIRE_TYPE),
        basis: toStr(data?.meta?.basis),
        subType: toStr(data?.meta?.subType),
      });
      setTdsPercent((prev) => (prev && toNum(prev) > 0 ? prev : toStr(v.tdsPercent ?? "0")));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Failed to load details");
    } finally {
      setPageLoading(false);
    }
  }, [vehicleId, loginUserId, branchId, vehicleOwnerType, commonHeaders, canUse]);

  const openChild = useCallback((path) => window.open(path, "_blank"), []);

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      syncFromLocalStorage();
      const saved = sessionStorage.getItem("billGeneration_selectedVehDetails");
      if (saved) {
        try {
          applyVehDetailsToProgress(JSON.parse(saved));
        } catch {
          /* ignore */
        }
      }
      await loadInit();
      toast.success("Header refreshed. Progress/spares kept from child screens.");
    } catch (err) {
      toast.error(err?.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, [syncFromLocalStorage, applyVehDetailsToProgress, loadInit]);

  const handleReset = useCallback(() => {
    setParticulars(defaultEditable.particulars);
    setAdvanceAmt(defaultEditable.advanceAmt);
    setShiftingAmt(defaultEditable.shiftingAmt);
    setMaterialDeduction(defaultEditable.materialDeduction);
    setMaintenanceReason(defaultEditable.maintenanceReason);
    setMaintenanceCharges(defaultEditable.maintenanceCharges);
    setOtherCharges(defaultEditable.otherCharges);
    setReasonForDeduction(defaultEditable.reasonForDeduction);
    setPreparedBy(defaultEditable.preparedBy);
    setIncludeDieselCost(defaultEditable.includeDieselCost);
    setProgress({ ...defaultProgress });
    setProgressIds([]);
    setProgressDetIds([]);
    setSparesIds([]);
    setSparesDetIds([]);
    setAdvanceIds([]);
    setShiftingIds([]);
    setSparesDeduction("0");
    sessionStorage.removeItem("billGeneration_selectedVehDetails");
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    toast.info("Cleared bill cart and typed fields");
  }, [STORAGE_KEY]);

  const handleSubmit = useCallback(async () => {
    try {
      syncFromLocalStorage();
      if (!progressIds.length) {
        toast.error("Select Vehicle Progress first. Bill rows are created only on Submit.");
        return;
      }
      setSubmitLoading(true);
      const payload = {
        vehicleId,
        branchId,
        loginUserId,
        brtype: vehicleOwnerType || HIRE_TYPE,
        billNo,
        billDate,
        particulars,
        preparedBy,
        advanceAmt,
        shiftingAmt,
        sparesDeduction,
        materialDeduction,
        sparesTotalAmount: String(sparesTotalAmount),
        maintenanceReason,
        maintenanceCharges,
        otherCharges,
        reasonForDeduction,
        tdsPercent,
        tdsAmount: String(tdsAmount),
        includeDieselCost,
        fuelIssued: progress.fuelIssued,
        totalDieselCost: progress.totalDieselCost,
        progress: {
          totalHrs: progress.totalHrs,
          totalKm: progress.totalKm,
          breakingHrs: progress.breakingHrs,
          hrAmount: progress.hrAmount,
          kmAmount: progress.kmAmount,
          breakingAmount: progress.breakingAmount,
          driverAdvance: progress.driverAdvance,
          bata: progress.bata,
          progressTotal: String(liveProgressTotal),
        },
        balancePayable: String(balancePayable),
        progressIds,
        progressDetIds:
          progressDetIds.length === progressIds.length
            ? progressDetIds
            : progressIds.map(() => DEFAULT_ID),
        sparesIds,
        sparesDetIds:
          sparesDetIds.length === sparesIds.length
            ? sparesDetIds
            : sparesIds.map(() => DEFAULT_ID),
        advanceIds,
        shiftingIds,
      };
      const { data } = await axios.post(`${API_BASE}/submit`, payload, { headers: commonHeaders });
      if (!data?.success) throw new Error(data?.message || "Submit failed");
      toast.success(`Bill Generated Successfully (Bill ID ${data.billId || ""})`);
      sessionStorage.removeItem("billGeneration_selectedVehDetails");
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      navigate("/vehicle/BillGenerationListing");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Submit failed");
    } finally {
      setSubmitLoading(false);
    }
  }, [
    syncFromLocalStorage,
    progressIds,
    progressDetIds,
    sparesIds,
    sparesDetIds,
    advanceIds,
    shiftingIds,
    vehicleId,
    branchId,
    loginUserId,
    vehicleOwnerType,
    billNo,
    billDate,
    particulars,
    preparedBy,
    advanceAmt,
    shiftingAmt,
    sparesDeduction,
    materialDeduction,
    sparesTotalAmount,
    maintenanceReason,
    maintenanceCharges,
    otherCharges,
    reasonForDeduction,
    tdsPercent,
    tdsAmount,
    includeDieselCost,
    progress,
    liveProgressTotal,
    balancePayable,
    commonHeaders,
    STORAGE_KEY,
    navigate,
  ]);

  useEffect(() => {
    if (!vehicleId) {
      toast.error("Missing vehicleId in URL");
      return;
    }
    loadInit();
    syncFromLocalStorage();
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) syncFromLocalStorage();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [vehicleId, loadInit, syncFromLocalStorage, STORAGE_KEY]);

  useEffect(() => {
    if (location.state?.vehDetails) applyVehDetailsToProgress(location.state.vehDetails);
    applyIdsFromSelectionObject(location.state);
  }, [location.state, applyVehDetailsToProgress, applyIdsFromSelectionObject]);

  useEffect(() => {
    const saved = sessionStorage.getItem("billGeneration_selectedVehDetails");
    if (!saved) return;
    try {
      applyVehDetailsToProgress(JSON.parse(saved));
    } catch {
      /* ignore */
    }
  }, [applyVehDetailsToProgress]);

  const Field = ({ label, children }) => (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
  const ReadBox = ({ value }) => <div className={readClass}>{toStr(value) || "-"}</div>;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL GENERATION ENTRY</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Hired Vehicle Bill Entry
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle ID: {vehicleId || "-"} • User ID: {loginUserId || "-"} • Bill No: {billNo || "-"} • Progress: {progressIds.length}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={() => navigate("/vehicle/BillGenerationListing")} disabled={isBusy} className={`${ghostBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaArrowLeft /> Back
              </button>
              <button type="button" onClick={handleRefresh} disabled={isBusy} className={`${ghostBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button type="button" onClick={handleReset} disabled={isBusy} className={`${ghostBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaUndo /> Reset
              </button>
              <button type="button" onClick={handleSubmit} disabled={isBusy} className={`${primaryBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSave /> {submitLoading ? "Submitting..." : "Submit Bill"}
              </button>
            </div>
          </div>

          <div className={panelClass}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={panelTitleClass}>Vehicle / Party Details</h3>
              <button type="button" onClick={() => setShowVehicle((s) => !s)} className={ghostBtn}>
                {showVehicle ? <FaChevronUp /> : <FaChevronDown />}
                {showVehicle ? "Hide" : "Show"}
              </button>
            </div>
            {showVehicle && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Bill No"><ReadBox value={billNo} /></Field>
                <Field label="Bill Date"><ReadBox value={billDate} /></Field>
                <Field label="Name of the Party"><ReadBox value={party.name} /></Field>
                <Field label="PAN Card No"><ReadBox value={party.pan} /></Field>
                <Field label="Mailing Address"><ReadBox value={party.address} /></Field>
                <Field label="Vehicle Name"><ReadBox value={veh.vehicleName} /></Field>
                <Field label="Vehicle Type"><ReadBox value={veh.vehicleType} /></Field>
                <Field label="Vehicle Basis"><ReadBox value={veh.vehicleBasis || vehMeta.basis} /></Field>
                <Field label="Vehicle Reg No"><ReadBox value={veh.regNo} /></Field>
                <Field label="Vehicle (Equipment)"><ReadBox value={veh.equipmentNo} /></Field>
              </div>
            )}
          </div>

          <div className={panelClass}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className={panelTitleClass}>Vehicle Progress</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={() => openChild(`/vehicle/BillAddVehicle?VehID=${encodeURIComponent(vehicleId)}&Type=4`)} className={`${primaryBtn} !px-4 !py-2`}>
                  <FaExternalLinkAlt /> Add Vehicle Progress
                </button>
                <button type="button" onClick={() => openChild(`/vehicle/BillAddAdvance?VehID=${encodeURIComponent(vehicleId)}&Type=1`)} className={`${primaryBtn} !px-4 !py-2`}>
                  <FaExternalLinkAlt /> Add Advance
                </button>
                <button type="button" onClick={() => openChild(`/vehicle/BillAddAdvance?VehID=${encodeURIComponent(vehicleId)}&Type=2`)} className={`${primaryBtn} !px-4 !py-2`}>
                  <FaExternalLinkAlt /> Add Shifting
                </button>
                <button type="button" onClick={() => setShowProgress((s) => !s)} className={ghostBtn}>
                  {showProgress ? <FaChevronUp /> : <FaChevronDown />}
                  {showProgress ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            {showProgress && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Total Hrs"><ReadBox value={progress.totalHrs} /></Field>
                <Field label="Total Km"><ReadBox value={progress.totalKm} /></Field>
                <Field label="Breaking Hours"><ReadBox value={progress.breakingHrs} /></Field>
                <Field label="Breaking Hours Amount"><ReadBox value={progress.breakingAmount} /></Field>
                <Field label="Hours Total Amount"><ReadBox value={progress.hrAmount} /></Field>
                <Field label="Km Total Amount"><ReadBox value={progress.kmAmount} /></Field>
                <Field label="Driver Advance"><ReadBox value={progress.driverAdvance} /></Field>
                <Field label="Party Advance"><ReadBox value={advanceAmt} /></Field>
                <Field label="Shifting Amount"><ReadBox value={shiftingAmt} /></Field>
                <Field label="Bata (Others)">
                  <input type="text" value={progress.bata} onChange={(e) => setProgress((p) => ({ ...p, bata: e.target.value }))} className={fieldClass} placeholder="0" />
                </Field>
                <Field label="TDS Deductions (%)">
                  <input type="text" value={tdsPercent} onChange={(e) => setTdsPercent(e.target.value)} className={fieldClass} placeholder="0" />
                </Field>
                <Field label="Progress Total"><ReadBox value={liveProgressTotal.toFixed(2)} /></Field>
                <Field label="TDS Deductions Value"><ReadBox value={tdsAmount.toFixed(2)} /></Field>
                <Field label="Prepared By">
                  <input type="text" value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} className={fieldClass} placeholder="Prepared by" />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Particulars">
                    <textarea rows={3} value={particulars} onChange={(e) => setParticulars(e.target.value)} className={fieldClass} placeholder="Enter particulars" />
                  </Field>
                </div>
              </div>
            )}
          </div>

          <div className={panelClass}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className={panelTitleClass}>Spares / Deductions</h3>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => openChild(`/vehicle/BillAddSpares?VehID=${encodeURIComponent(vehicleId)}&Type=3`)} className={`${primaryBtn} !px-4 !py-2`}>
                  <FaExternalLinkAlt /> Add Spares
                </button>
                <button type="button" onClick={() => setShowSpares((s) => !s)} className={ghostBtn}>
                  {showSpares ? <FaChevronUp /> : <FaChevronDown />}
                  {showSpares ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            {showSpares && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Spares Deductions"><ReadBox value={sparesDeduction} /></Field>
                <Field label="Spares Total"><ReadBox value={sparesTotalAmount.toFixed(2)} /></Field>
                <Field label="Maintenance Reason">
                  <input type="text" value={maintenanceReason} onChange={(e) => setMaintenanceReason(e.target.value)} className={fieldClass} />
                </Field>
                <Field label="Maintenance Charges">
                  <input type="text" value={maintenanceCharges} onChange={(e) => setMaintenanceCharges(e.target.value)} className={fieldClass} placeholder="0" />
                </Field>
                <Field label="Material Deductions">
                  <input type="text" value={materialDeduction} onChange={(e) => setMaterialDeduction(e.target.value)} className={fieldClass} placeholder="0" />
                </Field>
                <Field label="Reason for Deduction">
                  <input type="text" value={reasonForDeduction} onChange={(e) => setReasonForDeduction(e.target.value)} className={fieldClass} />
                </Field>
                <Field label="Other Charges">
                  <input type="text" value={otherCharges} onChange={(e) => setOtherCharges(e.target.value)} className={fieldClass} placeholder="0" />
                </Field>
                <Field label="Usage Cost Per Hr/KM"><ReadBox value={veh.usageCost} /></Field>
                <Field label="Fuel Issued"><ReadBox value={progress.fuelIssued} /></Field>
                <Field label="Fuel Rate"><ReadBox value={veh.fuelRate} /></Field>
                <Field label="Include Diesel Cost">
                  <div className="flex items-center gap-6 pt-2">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="diesel" checked={includeDieselCost === true} onChange={() => setIncludeDieselCost(true)} className="w-4 h-4 cursor-pointer" />
                      <span className={isDark ? "text-gray-200" : "text-gray-700"}>Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="diesel" checked={includeDieselCost === false} onChange={() => setIncludeDieselCost(false)} className="w-4 h-4 cursor-pointer" />
                      <span className={isDark ? "text-gray-200" : "text-gray-700"}>No</span>
                    </label>
                  </div>
                </Field>
                <Field label="Total Diesel Cost"><ReadBox value={progress.totalDieselCost} /></Field>
              </div>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Base Before TDS</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">TDS Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Diesel Included</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Balance Payable</th>
                </tr>
              </thead>
              <tbody>
                <tr className={isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-blue-50"}>
                  <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                    {baseBalanceBeforeTds.toFixed(2)}
                  </td>
                  <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                    {tdsAmount.toFixed(2)}
                  </td>
                  <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {includeDieselCost ? "Yes" : "No"}
                  </td>
                  <td className={`px-6 py-4 text-sm font-bold ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                    {balancePayable.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {isBusy && (
            <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading ? "Loading page..." : refreshing ? "Refreshing..." : submitLoading ? "Submitting..." : "Loading..."}
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BillGenerationEntry;